import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { Owned } from 'app/model/base/named';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { UntilActivity } from 'app/model/pipeline/until.activity';
import { generateUUID } from 'app/shared/utils/uuid.util';
import {
    IPipelineCopilotCurrentPipelineContext,
    IUntilActivityInterimContext,
    PipelineActivityInterimContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { Activity, arrayIsNullOrEmpty } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(UntilActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            expression: '*',
            timeout: '*',
            activities: '*'
        }
    }
})
export class UntilActivityAdapter extends ActivityAsyncAdapter<
    UntilActivity,
    Model.UntilActivity,
    IUntilActivityInterimContext
> {
    public override async toFabricCopilotContext(untilActivity: UntilActivity): Promise<IUntilActivityInterimContext> {
        if (untilActivity) {
            const untilActivityContext: IUntilActivityInterimContext = {
                ...(await super.toFabricCopilotContext(untilActivity)),
                StopExpression: UntilActivity.EXPRESSION.templateValue(untilActivity),
                Timeout: untilActivity.untilTimeout
            };

            const childActivities: PipelineActivityInterimContext[] = [];
            for (const activity of untilActivity.pipeline?.activities ?? []) {
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    Metamodel.modelOf(activity)
                );
                const activityContext = await adapter.toFabricCopilotContext(activity);
                if (activityContext) {
                    childActivities.push(activityContext);
                }
            }
            if (childActivities.length > 0) {
                untilActivityContext.ChildActivities = childActivities;
            }
            return untilActivityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IUntilActivityInterimContext,
        model?: UntilActivity
    ): Promise<UntilActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.StopExpression != null) {
                UntilActivity.EXPRESSION.setTemplateValue(model, context.StopExpression);
            }
            if (context.Timeout != null) {
                model.untilTimeout = context.Timeout;
            }
            if (!model.pipeline) {
                model.pipeline = Owned.create<Pipeline>(Pipeline.MODEL, model);
                model.pipeline.name = generateUUID();
            }
            if (!arrayIsNullOrEmpty(context.ChildActivities)) {
                const adapter = this.context.asyncAdapterFor<
                    Pipeline,
                    Model.PipelineResource,
                    IPipelineCopilotCurrentPipelineContext
                >(Pipeline.MODEL);
                await adapter.fromFabricCopilotContext(
                    {
                        Activities: context.ChildActivities
                    },
                    model.pipeline
                );
            } else {
                model.pipeline.activities.clear();
            }
        }
        return model;
    }

    public override async postToModel(activity: UntilActivity): Promise<void> {
        await super.postToModel(activity);

        await Promise.all(
            Array.from(activity.pipeline.activities.values()).map(async (innerActivity) => {
                const activityModel = Metamodel.modelOf(innerActivity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(innerActivity);
                } catch (e) {
                    this._logError(
                        `Activity "${innerActivity.name}" \`postToModel\` failed in until activity of pipeline "${activity.pipeline.name}"`,
                        e
                    );
                }
            })
        );
    }

    protected async _toResource(activity: UntilActivity): Promise<Model.UntilActivity> {
        const modelUntilActivity: Model.UntilActivity = super._getCommonActivityProperties(
            activity,
            'Until'
        ) as Model.UntilActivity;

        modelUntilActivity.typeProperties = modelUntilActivity.typeProperties || {
            expression: undefined,
            activities: []
        };
        modelUntilActivity.typeProperties.timeout = activity.untilTimeout;

        if (!ValueAdapter.isTemplated(activity, UntilActivity.EXPRESSION)) {
            // the expression propery only accepts expressions, turn the regular value into an expresion
            UntilActivity.EXPRESSION.setTemplateValue(activity, UntilActivity.EXPRESSION.value(activity));
        }

        modelUntilActivity.typeProperties.expression = ValueAdapter.toResource(activity, UntilActivity.EXPRESSION);

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const pipeline = await adapter.toResource(activity.pipeline);
            if (pipeline.properties) {
                modelUntilActivity.typeProperties.activities = pipeline.properties.activities;
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelUntilActivity, parsed);
        }

        return modelUntilActivity;
    }

    protected async _toModel(modelActivity: Model.UntilActivity): Promise<UntilActivity> {
        const activity: UntilActivity = UntilActivity.MODEL.create<UntilActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { expression: undefined, activities: [] };
        activity.untilTimeout = modelActivity.typeProperties.timeout;

        ValueAdapter.toModel(activity, UntilActivity.EXPRESSION, modelActivity.typeProperties.expression);

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);
        if (adapter) {
            const tempPipeline: Model.PipelineResource = {
                properties: { activities: modelActivity.typeProperties.activities }
            };
            activity.pipeline = Owned.create<Pipeline>(Pipeline.MODEL, activity);
            await adapter.toModel(tempPipeline, activity.pipeline);
            // Nested pipeline name is unnecessary for Until model, it's just used as a workaround of Pipeline naming validator.
            activity.pipeline.name = generateUUID();
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, UntilActivity.MODEL));
        return activity;
    }
}
